import { useState, useEffect, useRef } from "react";
import { CaretLeft, CaretRight, ArrowSquareOut, ShareNetwork, FilmSlate, Check } from "phosphor-react";
import { getReels } from "../../utils/api/reelApi";

const CONTENT = {
  ne: {
    eyebrow: "सामाजिक सञ्जाल",
    title: "इटहरीको आवाज",
    subtitle: "फेसबुकमा पोस्ट भएका पछिल्ला रिलहरू, एक ठाउँमा।",
  },
  en: {
    eyebrow: "Social Voice",
    title: "Voice of Itahari",
    subtitle: "Our latest reels from Facebook, all in one place.",
  },
};

// ── Carousel tuning ─────────────────────────────────────────────────────────
const CARD_WIDTH = 268;
const CARD_HEIGHT = 476; 
const GAP = 30;
const SPEED = 0.45;
const RESUME_DELAY = 3000;

export default function VoiceOfItahari({ language = "ne" }) {
  const t = CONTENT[language] || CONTENT.ne;
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);

  useEffect(() => {
    getReels(20)
      .then((data) => setReels(Array.isArray(data) ? data : []))
      .catch(() => setReels([]))
      .finally(() => setLoading(false));
  }, []);

  const singleSetWidth = reels.length * (CARD_WIDTH + GAP);
  // duplicate the set just enough to fill the viewport for a seamless loop
  const repeatCount = reels.length > 0
    ? Math.min(3, Math.max(2, Math.ceil(1600 / singleSetWidth)))
    : 0;
  const loopReels = repeatCount > 0 ? Array.from({ length: repeatCount }, () => reels).flat() : [];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || reels.length === 0) return;

    function step() {
      if (!pausedRef.current && el) {
        el.scrollLeft += SPEED;
        if (el.scrollLeft >= singleSetWidth) {
          el.scrollLeft -= singleSetWidth;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reels, singleSetWidth]);

  useEffect(() => () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  const pause = () => {
    pausedRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  };

  const resumeNow = () => { pausedRef.current = false; };

  const scheduleResume = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => { pausedRef.current = false; }, RESUME_DELAY);
  };

  const scrollBy = (dir) => {
    if (!scrollRef.current) return;
    pause();
    scrollRef.current.scrollBy({ left: dir * (CARD_WIDTH + GAP) * 2, behavior: "smooth" });
    scheduleResume();
  };

  const openOnFacebook = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareReel = async (reel) => {
    const shareId = reel._id || reel.fbId;
    const shareData = {
      title: reel.caption || (language === "ne" ? "फेसबुक रिल" : "Facebook Reel"),
      url: reel.permalinkUrl,
    };

    // mobile/supported browsers: native share sheet
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
      
        if (err?.name === "AbortError") return;
      }
    }

    
    try {
      await navigator.clipboard.writeText(reel.permalinkUrl);
      setCopiedId(shareId);
      setTimeout(() => setCopiedId((cur) => (cur === shareId ? null : cur)), 1800);
    } catch (err) {
    
      openOnFacebook(reel.permalinkUrl);
    }
  };

  if (!loading && reels.length === 0) return null;

  return (
    <section
      className="bg-white px-6 py-20"
      onMouseEnter={pause}
      onMouseLeave={resumeNow}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-2 inline-flex items-center gap-2">
              <span className="h-[2px] w-5 rounded-full bg-[#2D7A3A]" />
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#2D7A3A]">
                {t.eyebrow}
              </span>
            </div>
            <h2 className="text-2xl font-extrabold text-[#1A1815] md:text-3xl">{t.title}</h2>
            <p className="mt-1 text-sm text-[#6b7280]">{t.subtitle}</p>
          </div>

          {reels.length > 3 && (
            <div className="flex gap-2">
              <button
                onClick={() => scrollBy(-1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-[#1A1815] shadow-sm transition hover:bg-gray-50"
              >
                <CaretLeft size={17} weight="bold" />
              </button>
              <button
                onClick={() => scrollBy(1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-[#1A1815] shadow-sm transition hover:bg-gray-50"
              >
                <CaretRight size={17} weight="bold" />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">…</div>
        ) : (
          <div
            ref={scrollRef}
            onTouchStart={pause}
            onTouchEnd={scheduleResume}
            style={{ gap: GAP }}
            className="flex overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loopReels.map((reel, idx) => (
              <div
                key={`${reel._id || reel.fbId}-${idx}`}
                className="group relative shrink-0 overflow-hidden rounded-2xl bg-black shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
                style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
              >
            
                <iframe
                  src={`https://www.facebook.com/plugins/video.php?height=${CARD_HEIGHT}&href=${encodeURIComponent(
                    reel.permalinkUrl
                  )}&show_text=false&width=${CARD_WIDTH}&t=0`}
                  width={CARD_WIDTH}
                  height={CARD_HEIGHT}
                  style={{ border: "none", overflow: "hidden", display: "block" }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  loading="lazy"
                  title={reel.caption || "Facebook reel"}
                />

                <div className="absolute bottom-3 right-3 z-10 flex gap-2">
                  <button
                    onClick={() => openOnFacebook(reel.permalinkUrl)}
                    title={language === "ne" ? "फेसबुकमा हेर्नुहोस्" : "Open on Facebook"}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-md transition-transform hover:scale-110"
                  >
                    <ArrowSquareOut size={15} weight="bold" className="text-[#166534]" />
                  </button>
                </div>

                <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center bg-gradient-to-br from-[#1e6b3a] to-[#052e16] text-white/25">
                  <FilmSlate size={40} weight="light" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}